import { createRef, useMemo, useState } from "react";
import { Button, Card, LinearProgress, SxProps, Typography, Theme, Box } from "@mui/material";
import { Document, pdfjs } from "react-pdf";
import { StateHandle } from "../StateHandle";
import { DocumentIntake, DocumentSegment } from "generated/graphql";
import DocumentViewerPage from "./DocumentViewer/DocumentViewerPage";
import Flex from "./Flex";
import { PageCallback } from "react-pdf/dist/cjs/shared/types.d.ts";
import { Annotation } from "components/AnnotationInterface";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.mjs`;

interface LoadSegmentProps {
  intakePageNumber: number;
  canvasScrollContainer?: HTMLElement;
  setPageNumber: (page: number) => void;
  setVisiblePageNumber: (page: number) => void;
  position: "before" | "after";
}
const LoadSegmentButton = (props: LoadSegmentProps) => {
  const { intakePageNumber, canvasScrollContainer, setPageNumber, setVisiblePageNumber, position } = props;
  const sx: SxProps<Theme> = position === "after" ? { mb: 1 } : { mt: 1 };
  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Button
        variant="contained"
        color="primary"
        sx={sx}
        onClick={() => {
          setPageNumber(intakePageNumber + (position === "after" ? 1 : -1));
          setVisiblePageNumber(intakePageNumber + (position === "after" ? 1 : -1));
          if (canvasScrollContainer !== undefined) {
            setTimeout(() => {
              canvasScrollContainer.scrollTop = position === "after" ? 0 : canvasScrollContainer.scrollHeight;
            }, 250);
          }
        }}>
        Load {position === "after" ? "Next" : "Previous"} Pages
      </Button>
    </Box>
  );
};

const Placeholder = (props: { msg?: string; pageSize: SxProps<Theme> }) => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
    <Card sx={props.pageSize}>
      <Typography variant="h6">
        <b>{props.msg}</b>
      </Typography>
    </Card>
  </Box>
);

const LoadingProgress = (props: { loadedPercent: number }) => {
  return (
    <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
      <LinearProgress sx={{ width: 200 }} variant="determinate" color="success" value={props.loadedPercent} />
    </Flex>
  );
};

export interface PDFInfo {
  numPages: number;
  // width: number;
  // height: number;
}

interface OnPDFClick {
  x: number;
  y: number;
  pixelX: number;
  pixelY: number;
}

export interface PDFCanvasProps {
  pdfUrl: string;
  pdfSize: number;
  pageNumber: number;
  intakePageNumber: number;
  setVisiblePageNumber: (page: number) => void;
  visiblePageNumber: number | undefined;
  segment: DocumentSegment;
  scale: number;
  annotations?: Annotation[];
  className?: string;
  intake: DocumentIntake;
  userRotateState: StateHandle<number>;
  onLoad?: (info: PDFInfo) => void;
  onClick?: (coordinates: OnPDFClick) => void;
  onHover?: (x: number, y: number) => void;
  onDrag?: (x: number, y: number) => void;
  onDragStart?: (x: number, y: number) => void;
  onDragEnd?: (x: number, y: number) => void;
  setPageProxy: (page: PageCallback | undefined) => void;
  pageProxy: PageCallback | undefined;
  setPageNumber: (pageNumber: number) => void;
  addDocumentViewerPageRef: (intakePageNumber: number, ref: HTMLCanvasElement) => void;
  removeDocumentViewerPageRef: (intakePageNumber: number) => void;
  canvasScrollContainer: HTMLElement | undefined;
}

const PDFCanvas3 = (props: PDFCanvasProps) => {
  const { scale, setPageProxy } = props;
  const rootRef = createRef<HTMLDivElement>();
  const [loadedPercent, setLoadedPercent] = useState(0);
  const [userRotate] = props.userRotateState;
  const [pageRotate, setPageRotate] = useState(0);
  const { segment, canvasScrollContainer } = props;

  const segmentPageNumbers = useMemo(() => {
    const pageNumbers: number[] = [];
    for (let i = 0; i < segment.pageCount; i++) {
      pageNumbers.push(i + 1);
    }

    return pageNumbers;
  }, [segment.id]);
  const pageSize: SxProps<Theme> = useMemo(() => {
    if (props.pageProxy) {
      return {
        width: props.pageProxy.width,
        height: props.pageProxy.height,
      };
    } else {
      return {
        width: 400,
        height: 400,
      };
    }
  }, [props.pageProxy?.view[2], props.pageProxy?.view[3]]);

  const onLoadProgress = (loaded: number, total: number) => {
    const perc = (loaded / total) * 100;
    setLoadedPercent(perc);
  };

  const pdfInput = useMemo(
    () => ({
      url: props.pdfUrl,
      withCredentials: true,
    }),
    [props.pdfUrl]
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
      }}>
      <Document
        className="docRoot"
        inputRef={rootRef}
        file={pdfInput}
        options={{
          // disableAutoFetch: true,
          // disableStream: true,
          rangeChunkSize: 10485760,
          length: props.pdfSize,
        }}
        loading={<LoadingProgress loadedPercent={loadedPercent} />}
        error={<Placeholder msg="Error" pageSize={pageSize} />}
        noData={<Placeholder msg="Loading" pageSize={pageSize} />}
        onLoadSuccess={(pdfProxy) => {
          if (props.onLoad) {
            props.onLoad({ numPages: pdfProxy.numPages });
          }
        }}
        {...{
          onLoadProgress: (progress: any) => {
            onLoadProgress(progress.loaded, progress.total);
          },
        }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px 16px" }}>
          {props.intakePageNumber > 1 && (
            <LoadSegmentButton
              intakePageNumber={props.intakePageNumber}
              position="before"
              canvasScrollContainer={canvasScrollContainer}
              setPageNumber={props.setPageNumber}
              setVisiblePageNumber={props.setVisiblePageNumber}
            />
          )}
          {segmentPageNumbers.map((segmentPageNumber) => {
            const intakePageNumber = segment.startPage + segmentPageNumber - 1;
            return (
              <DocumentViewerPage
                loadingSx={pageSize}
                setVisiblePageNumber={props.setVisiblePageNumber}
                addDocumentViewerPageRef={props.addDocumentViewerPageRef}
                removeDocumentViewerPageRef={props.removeDocumentViewerPageRef}
                key={intakePageNumber}
                scale={scale}
                userRotate={userRotate}
                pageRotate={pageRotate}
                intakePageNumber={intakePageNumber}
                paginatorPageNumber={props.pageNumber}
                segmentPageNumber={segmentPageNumber}
                onHover={props.onHover}
                setPageRotate={setPageRotate}
                setPageProxy={setPageProxy}
                annotations={intakePageNumber === props.visiblePageNumber ? props.annotations : []}
              />
            );
          })}
          {props.intakePageNumber < props.intake.pages && (
            <LoadSegmentButton
              position="after"
              setPageNumber={props.setPageNumber}
              intakePageNumber={props.intakePageNumber}
              canvasScrollContainer={canvasScrollContainer}
              setVisiblePageNumber={props.setVisiblePageNumber}
            />
          )}
        </Box>
      </Document>
    </Box>
  );
};

export default PDFCanvas3;
